import { Controller } from 'stimulus';
import { prepareEventData } from './alerts_controller';
import { FetchRequest } from '@rails/request.js';
import { AbortableRequest, debounce } from '@/js/util';

const abortError = (description) => new DOMException(description, 'AbortError');

// Tail promise per queue name, shared by every form that opted into that queue
const queues = new Map();

/**
 * Handles form autosubmit on various actions, with optional integration with Alerts
 * @example Basic usage
 *   form[data-controller="autosubmit" data-autosubmit-response-kind-value="json" action="/users" method="post"]
 *    /! submits as you type
 *    input[data-action="autosubmit#submit"]
 *    /! `handler` targets become disabled until request finishes
 *    input[data-autosubmit-target="handler" data-action="autosubmit#submit"]
 *    /! `#reset` action sets form to it's initial state
 *    button[data-autosubmit-target="handler" data-action="autosubmit#reset"] Clear
 *
 * The `data-autosubmit-response-kind-value` is a required attribute to make it work,
 * it currently supports two values:
 * - json (it would look for `id` and `html` keys in the response and replace an
 *   element found by `id` with `html`, this is a legacy approach from pre-Turbo era)
 * - turbo-stream (in this case response is expected to contain turbo stream and
 *   will be handled by Turbo)
 *
 * There are two available actions: `#submit` and `#submitDebounced`. For the latter
 * to work, you have to specify `debounce` value with milliseconds of delay.
 * @example Debouncing
 *   form[data-controller="autosubmit" data-autosubmit-debounce-value="500"]
 *     /! submits when user interacts with input then waits for half a second
 *     /! or when user presses Tab - then submit will be immediate
 *     input[data-action="autosubmit#submitDebounced keydown.tab->autosubmit#submit"]
 *
 * @example Protecting a field from turbo-stream morphs while the user types
 *   /! `#lock` marks the focused field with data-turbo-permanent so a stale
 *   /! turbo-stream morph can't overwrite what the user is typing; `#unlock`
 *   /! releases it and flushes any not-yet-submitted input right away
 *   input[data-action="autosubmit#submitDebounced focus->autosubmit#lock blur->autosubmit#unlock"]
 *
 * While a submit is scheduled or in flight, the form carries `data-autosubmit-pending` as a
 * styling hook for pending UI.
 *
 * @example Sending a request the form doesn't describe (e.g. a delete button inside the form)
 *   /! `#request` performs a body-less request to the given url and method, with the same
 *   /! pending flag, cancellation and queue handling as `#submit`
 *   button[type="button" data-action="autosubmit#request"
 *          data-autosubmit-url-param="/installments/1" data-autosubmit-method-param="delete"]
 *
 * @example Serialising requests across forms
 *   /! forms sharing a `queue` value send their requests one at a time, in submit order, so a
 *   /! slow response can never overwrite the DOM rendered from a newer one
 *   form[data-controller="autosubmit" data-autosubmit-queue-value="installments_invoice_1"]
 *   form[data-controller="autosubmit" data-autosubmit-queue-value="installments_invoice_1"]
 *
 * @example (with json response kind) Display alert on success (if some parent element has Alerts controller)
 *   /! the parent listens with data-action="autosubmit:alert->alerts#show"
 *   form[data-controller="autosubmit" action="/users" method="post"]
 *     template[data-autosubmit-target="alertSuccess"]
 *       div.flash Success!
 */
export default class extends Controller {
  static targets = ['handler', 'alertSuccess'];

  static values = {
    debounce: Number,
    responseKind: String,
    queue: String,
  };

  #dirty = false;
  #request = null;

  connect() {
    if (!(this.element instanceof HTMLFormElement)) {
      throw new Error('autosubmit controller should be assigned to the form only!');
    }

    if (this.debounceValue) {
      const debounced = debounce(this.submit.bind(this), this.debounceValue);
      this.submitDebounced = (event) => {
        this.#dirty = true;
        this.#trackPending();
        debounced(event);
      };
      this.submitDebounced.cancel = debounced.cancel;
    }

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.element.addEventListener('submit', this.handleFormSubmit);
  }

  disconnect() {
    this.element.removeEventListener('submit', this.handleFormSubmit);
    this.submitDebounced.cancel && this.submitDebounced.cancel();
    this.#request?.cancel(abortError('controller disconnecting'));
  }

  submit(event) {
    const { extraPayload } = event?.detail || {};
    this.#perform(this._prepareRequest(extraPayload));
  }

  request({ params: { url, method } }) {
    this.#perform(new FetchRequest(method, url, { responseKind: this.responseKindValue }));
  }

  #perform(fetchRequest) {
    this.submitDebounced.cancel && this.submitDebounced.cancel();
    this.#dirty = false;
    this.#trackPending();
    this.#request?.cancel(abortError('subsequent request about to be made'));

    const request = new AbortableRequest(fetchRequest);
    this.#request = request;
    // inputs are disabled only now, after the caller captured the form data,
    // as disabled inputs would not appear in FormData object
    this.#disabled = true;
    this.dispatch('before-submit');

    this.#enqueue(() =>
      // superseded while waiting for its turn: fetch would reject it anyway, skip the round trip
      request.signal.aborted ? Promise.reject(request.signal.reason) : request.perform()
    )
      .then((response) => {
        if (response.ok) {
          this.dispatch('success');
        }

        switch (this.responseKindValue) {
          case 'turbo-stream':
            return null;
          case 'json':
            return this.#handleJSON(response);
          default:
            throw new Error(`unable to handle response of a kind ${this.responseKindValue}`);
        }
      })
      .catch((e) => (e.name === 'AbortError' ? console.debug(e) : console.error(e)))
      .finally(() => {
        if (this.#request !== request) {
          return;
        }
        this.#request = null;
        this.#clearPending();
        this.dispatch('completed');
        this.#disabled = false;
      });
  }

  // Runs `perform` once every request already queued under the same name has settled
  #enqueue(perform) {
    if (!this.queueValue) {
      return perform();
    }

    const previous = queues.get(this.queueValue) || Promise.resolve();
    const current = previous.then(perform);
    // the stored tail never rejects, so a failed request doesn't block the ones behind it
    const tail = current.catch(() => null);
    queues.set(this.queueValue, tail);
    return current;
  }

  submitDebounced() {
    throw new Error('you need to provide `debounce` value > 0 in order to use debounced function');
  }

  reset() {
    this.element.reset();
    this.submit();
  }

  lock(event) {
    event.target.setAttribute('data-turbo-permanent', '');
  }

  unlock(event) {
    event.target.removeAttribute('data-turbo-permanent');
    if (this.#dirty) {
      this.element.requestSubmit();
    }
  }

  /**
   * @param {SubmitEvent} e
   */
  handleFormSubmit(e) {
    e.preventDefault();
    this.submit(e);
  }

  #trackPending() {
    this.element.setAttribute('data-autosubmit-pending', '');
  }

  #clearPending() {
    this.element.removeAttribute('data-autosubmit-pending');
  }

  set #disabled(value) {
    this.handlerTargets.forEach((element) => {
      element.disabled = value;
    });
  }

  get #url() {
    return this.element.action;
  }

  get #method() {
    return this.element.method;
  }

  _prepareRequest(extraPayload) {
    const request = new FetchRequest(this.#method, this.#url, {
      responseKind: this.responseKindValue,
    });

    const data = new FormData(this.element);

    if (extraPayload?.length > 0) {
      extraPayload.forEach(({ name, value }) => data.append(name, value));
    }

    if (this.#method === 'get') {
      request.options.query = data;
    } else {
      request.options.body = data;
    }

    return request;
  }

  #handleJSON(response) {
    return response.json.then(({ id, html }) => {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }

      if (this.hasAlertSuccessTarget) {
        this.dispatch('alert', prepareEventData(this.alertSuccessTarget));
      }

      const template = document.createElement('template');
      template.innerHTML = html;
      // if there was an alert shown, give event a time to bubble up
      setTimeout(() => element.replaceWith(template.content));
    });
  }
}
